/**
 * Smart WhatsApp launcher.
 *
 * Browsers don't expose any API to check whether WhatsApp is installed
 * (privacy). The well-established pattern is:
 *   1. Trigger the `whatsapp://` custom-protocol URL — if WhatsApp (mobile
 *      app or desktop client) is installed, the OS hands the click off to it
 *      and the browser tab loses focus / becomes hidden.
 *   2. After a short timeout (~700 ms), if the page is STILL visible, we
 *      assume WhatsApp isn't installed and we fall back to `https://wa.me/...`
 *      which routes the user to WhatsApp Web (after a QR scan if needed).
 *
 * This delivers exactly the UX requested: "open the app instantly if it's
 * there, otherwise open the wa.me link like today".
 *
 * Usage:
 *   import { openWhatsApp } from '@/lib/whatsapp';
 *   openWhatsApp({ phone: '+919812345678', message: 'Hello!' });
 */

/** Strip everything except digits — wa.me expects digits-only. */
function sanitizePhone(raw) {
  return String(raw || '').replace(/[^0-9]/g, '');
}

/** Mobile devices get the native-app protocol dance; everything else is desktop. */
function isMobileDevice() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

/** Dedicated named browser tab reused for every WhatsApp Web send. */
const WA_WEB_WINDOW_NAME = 'shakti_whatsapp_web';

/** Direct WhatsApp Web composer URL (skips the wa.me interstitial). */
function buildWhatsAppWebAppUrl(phone, message) {
  const digits = sanitizePhone(phone);
  const text = encodeURIComponent(message || '');
  const params = [digits ? `phone=${digits}` : '', text ? `text=${text}` : ''].filter(Boolean).join('&');
  return `https://web.whatsapp.com/send${params ? `?${params}` : ''}`;
}

/**
 * Build the canonical wa.me fallback URL.
 */
export function buildWhatsAppWebUrl(phone, message) {
  const digits = sanitizePhone(phone);
  const text = encodeURIComponent(message || '');
  return digits
    ? `https://wa.me/${digits}${text ? `?text=${text}` : ''}`
    : `https://wa.me/${text ? `?text=${text}` : ''}`;
}

/**
 * Build the native-protocol URL that the OS / desktop app intercepts.
 */
function buildWhatsAppAppUrl(phone, message) {
  const digits = sanitizePhone(phone);
  const text = encodeURIComponent(message || '');
  if (digits) {
    return `whatsapp://send?phone=${digits}${text ? `&text=${text}` : ''}`;
  }
  return `whatsapp://send${text ? `?text=${text}` : ''}`;
}

/**
 * Open WhatsApp — tries the native app first, falls back to wa.me if not
 * installed.  Safe to call from any event handler.
 *
 * @param {Object} opts
 * @param {string} opts.phone   E.164 phone (with/without +); will be cleaned.
 * @param {string} opts.message Optional pre-filled message.
 */
export function openWhatsApp({ phone, message } = {}) {
  const appUrl = buildWhatsAppAppUrl(phone, message);
  const webUrl = buildWhatsAppWebUrl(phone, message);

  // Fallback when the native app is not installed. On desktop we open the
  // WhatsApp Web composer directly (skips the wa.me interstitial) in a
  // dedicated named tab. NOTE: web.whatsapp.com sets COOP, so true tab
  // reuse is browser-blocked — the real time-saver on a PC is the desktop
  // app, which we always try FIRST and which reuses its single instance.
  const openFallback = () => {
    if (!isMobileDevice()) {
      let win = null;
      try { win = window.open(buildWhatsAppWebAppUrl(phone, message), WA_WEB_WINDOW_NAME); } catch { /* popup blocked */ }
      if (win) {
        try { win.focus(); } catch { /* cross-origin focus can throw — ignore */ }
        return;
      }
    }
    window.open(webUrl, '_blank', 'noopener,noreferrer');
  };

  let handled = false;
  const markHandled = () => { handled = true; };

  // If the browser tab becomes hidden, the OS has handed off to WhatsApp →
  // we should NOT fall back.
  const onVisibilityChange = () => {
    if (document.hidden) markHandled();
  };
  const onBlur = () => markHandled();
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('blur', onBlur);

  // Use a transient iframe to attempt the protocol. This avoids the
  // page-navigation flicker / error toast that some browsers show when
  // assigning `window.location` to an unhandled scheme.
  let frame = null;
  try {
    frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.src = appUrl;
    document.body.appendChild(frame);
  } catch {
    /* iframe creation should never fail; just continue */
  }

  // Some browsers (Safari, older Chrome on Android) block iframe-launched
  // schemes — set window.location as a belt-and-braces second attempt.
  // We wrap in try/catch because Firefox throws if the scheme is unhandled.
  try { window.location.href = appUrl; } catch { /* ignore */ }

  // Decide the fallback after the OS has had a chance to switch apps.
  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('blur', onBlur);
    if (frame && frame.parentNode) frame.parentNode.removeChild(frame);

    if (!handled && !document.hidden) {
      // Still here → no WhatsApp app installed → browser fallback.
      openFallback();
    }
  }, 700);
}
